import React, { useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextField } from '@mui/material';
import { Event, RoomBooking } from '../../services/Types';
import { formatDate, convertDbTimeIntoInput } from '../../services/Helper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { flexItemColumn } from '../../global';
import { Moment } from 'moment';

interface EditorDateTimeProps {
  event: Event;
  setSelectedEvent: (event: Event) => void;
}

export const EditorDateTime: React.FC<EditorDateTimeProps> = ({ event, setSelectedEvent }): JSX.Element => {
  const [isAllDay, setIsAllDay] = useState<{ [key: number]: boolean }>({});

  const handleBookingDateChange = (bookingId: number, newDate: Moment | null) => {
    if (!newDate) return;
    
    const updatedBookings = event.roomBooking.map(booking => 
      booking.id === bookingId 
        ? { ...booking, date: newDate.valueOf() }
        : booking
    );

    setSelectedEvent({
      ...event,
      roomBooking: updatedBookings
    });
  };

  const handleTimeChange = (bookingId: number, field: 'startTime' | 'endTime', value: string) => {
    const [hours, minutes] = value.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;

    const updatedBookings = event.roomBooking.map(booking => 
      booking.id === bookingId 
        ? { ...booking, [field]: timeInMinutes }
        : booking
    );

    setSelectedEvent({
      ...event,
      roomBooking: updatedBookings
    });
  };

  const handlePublicationDateChange = (timestamp: number) => {
    setSelectedEvent({
      ...event,
      publicationDate: new Date(timestamp) // due to AdapterMoment
    });
  };

  const handleAllDayChange = (bookingId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsAllDay(prev => ({ ...prev, [bookingId]: checked }));

    if (checked) {
      const updatedBookings = event.roomBooking.map(booking => 
        booking.id === bookingId 
          ? { ...booking, startTime: 0, endTime: 23 * 60 + 59 }
          : booking
      );

      setSelectedEvent({
        ...event,
        roomBooking: updatedBookings
      });
    }
  };

  // Note: we are using AdapterMoment because it can handle UTC dates for the DatePicker
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {event.roomBooking.map((booking: RoomBooking) => {
        let bookingCategory = booking.category === 'dry' ? 'Dry Run' : booking.category;
        bookingCategory = (bookingCategory === 'live' && event.publicationDate) ? 'Recording' : bookingCategory;

        return (
          <Box key={booking.id}>
            <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
              {bookingCategory} Event
            </Typography>
            
            <Grid container spacing={0}>
              <Grid item xs={12} md={12}>
                <DatePicker
                  label="Select date"
                  value={formatDate(booking.date, 'moment') as Moment}
                  onChange={() => {}}
                  onAccept={(newValue) => handleBookingDateChange(booking.id, newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} md={12}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: '65%' }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={convertDbTimeIntoInput(booking.startTime)}
                      onChange={(e) => handleTimeChange(booking.id, 'startTime', e.target.value)}
                      disabled={isAllDay[booking.id]}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />

                    <TextField
                      label="End Time"
                      type="time"
                      value={convertDbTimeIntoInput(booking.endTime)}
                      onChange={(e) => handleTimeChange(booking.id, 'endTime', e.target.value)}
                      disabled={isAllDay[booking.id]}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Box>
                  <Box style={{ ...flexItemColumn, width: '35%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllDay[booking.id] || false}
                          onChange={handleAllDayChange(booking.id)}
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 24 },
                          }}
                        />
                      }
                      label="All Day"
                      sx={{ marginBottom: '25px' }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {event.publicationDate && (
        <Grid item xs={12} md={12}>
          <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
            Publication Date
          </Typography>
          <DatePicker
            label="Select date"
            value={formatDate(event.publicationDate.valueOf(), 'moment') as Moment}
            onChange={(newValue) => handlePublicationDateChange(newValue?.valueOf() ?? 0)}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </Grid>
      )}

    </LocalizationProvider>
  );
}; 